import { ORGANIZATION_LOCATIONS_ACTIVITIES_URL, ORGANIZATION_MAP_URL, WORK_ORDERS_URL } from '~/app_constants';
import { camelCase } from 'lodash-es';
import Resource from '~/resource';

export const ReportsResource = new Resource('/reports/:organizationId/:view/:reportName', {
  only: ['get'],
  extra: {
    chart: {
      method: 'GET',
      url: '/reports/:organizationId/:view/:chart',
    },
    sendReport: {
      method: 'POST',
      url: '/reports/:organizationId/send_report',
    },
    sendReportCheck: {
      method: 'GET',
      url: '/reports/:organizationId/send_report_check',
    },
    showScheduleReportToast: {
      method: 'GET',
      url: '/reports/:organizationId/show_schedule_report_toast',
    },
    members: {
      method: 'POST',
      url: '/reports/:organization_id/members',
    },
    projects: {
      method: 'POST',
      url: '/reports/:organization_id/projects',
    },
  },
  links: {
    reports: '/reports/:reportName?date=:date&date_end=:dateEnd',
  },
});
export const SavedReportFiltersResource = new Resource('/saved_report_filters/:id');
export const ScheduledReportsResource = new Resource('/scheduled_reports/:id', {
  only: ['create', 'update', 'destroy'],
  extra: {
    preview: {
      method: 'POST',
      url: '/scheduled_reports/preview',
    },
    enable: {
      method: 'POST',
      url: '/scheduled_reports/:id/enable',
    },
    disable: {
      method: 'POST',
      url: '/scheduled_reports/:id/disable',
    },
  },
});

export const ProjectsResource = new Resource('/projects/:id/', {
  extra: {
    get: {
      method: 'GET',
      url: '/projects/:id',
    },
    create: {
      method: 'POST',
      url: '/organizations/:id/projects',
    },
    restore: {
      method: 'POST',
      url: '/projects/:id/activate',
    },
    restoreMultiple: {
      method: 'POST',
      url: '/organizations/:id/projects/restore_multiple',
    },
    batchUpdate: {
      method: 'POST',
      url: '/organizations/:id/projects/batch_update.json',
    },
    update: {
      method: 'POST',
      url: '/projects/:id/unified_dialog_update',
    },
    updateBudget: {
      method: 'POST',
      url: '/projects/:id/update_budget',
    },
    removeBudget: {
      method: 'DELETE',
      url: '/projects/:id/remove_budget',
    },
    unlink: {
      method: 'POST',
      url: '/projects/:id/unlink',
    },
    delete: {
      method: 'DELETE',
      url: '/projects/:id/',
    },
    membersNotInProject: {
      // POST: member_ids can be tens of thousands of ids (post-"Select all"), which would overflow
      // the query string as a GET.
      method: 'POST',
      url: '/projects/:id/members_not_in_project',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organization_id/projects/members',
    },
  },
  links: {
    show: '/projects/:id',
    archiveDialog: '/projects/:id/archive_dialog.dialog',
    archiveMultipleDialog: '/organizations/:id/projects/archive_multiple_dialog.dialog',
    duplicateDialog: '/projects/:id/duplicate_project_dialog.dialog?origin=project_index',
    linkDialog: '/projects/:id/link_dialog.dialog',
    transferDialog: '/projects/:id/new_transfer.dialog',
    deleteDialog: '/projects/:id/delete_dialog.dialog',
  },
});

export const ClientsResource = new Resource('/clients/:id', {
  extra: {
    get: {
      method: 'GET',
      url: '/clients/:id',
    },
    create: {
      method: 'POST',
      url: '/organizations/:id/clients/unified_dialog_create',
    },
    update: {
      method: 'POST',
      url: '/clients/:id/unified_dialog_update',
    },
    load: {
      method: 'GET',
      url: '/organizations/:organizationId/clients',
    },
    archive: {
      method: 'POST',
      url: '/clients/:id/archive',
    },
    restore: {
      method: 'POST',
      url: '/clients/:id/restore',
    },
    archiveMultiple: {
      method: 'POST',
      url: '/organizations/:organizationId/clients/archive_multiple',
    },
    restoreMultiple: {
      method: 'POST',
      url: '/organizations/:organizationId/clients/restore_multiple',
    },
    projects: {
      method: 'GET',
      url: '/organizations/:organization_id/clients/projects',
    },
  },
});

export const ReferralResource = new Resource('/referral/:id/data', {
  only: ['get'],
});

export const UserResource = new Resource('/user', {
  extra: {
    insightsRegistration: {
      method: 'PATCH',
      url: '/user/hubstaff_insights_registration',
    },
    markBenchmarksDialogSeen: {
      method: 'PATCH',
      url: '/user/benchmarks_has_seen_dialog',
    },
    hideActivityInfoBanner: {
      method: 'PATCH',
      url: '/user/hide_activity_info_banner',
    },
    sendReferralInvite: {
      method: 'POST',
      url: '/user/send_referral_email',
    },
    retrieveReferralCode: {
      method: 'GET',
      url: '/user/retrieve_referral_code',
    },
  },
});

export const ManualTimeRequestsResource = new Resource('/manual_time_requests', {
  extra: {
    show: {
      method: 'GET',
      url: '/organizations/:organization_id/manual_time_requests/:id',
    },
    deny: {
      method: 'PATCH',
      url: '/organizations/:organization_id/manual_time_requests/:id/deny',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organization_id/manual_time_requests/members',
    },
  },
});

export const UserOrganizationsResource = new Resource('/user_organizations/:id', {
  extra: {
    enableTracking: {
      method: 'POST',
      url: '/user_organizations/:id/enable_tracking',
    },
    disableTracking: {
      method: 'POST',
      url: '/user_organizations/:id/disable_tracking',
    },
    updateSettingMultiple: {
      method: 'PATCH',
      url: '/organizations/:organizationId/user_organizations/update_setting_multiple',
    },
    rates: {
      method: 'GET',
      url: '/user_organizations/:id/rates',
    },
    exportForUpdateSchema: {
      method: 'GET',
      url: '/organizations/:organizationId/user_organizations/export_for_update_schema',
    },
    dismissPromotion: {
      method: 'PATCH',
      url: '/user_organizations/:id/dismiss_promotion',
    },
    dismissDialog: {
      method: 'POST',
      url: '/user_organizations/:id/dismiss_dialog',
    },
    updatePayPeriodsMultiple: {
      method: 'POST',
      url: '/organizations/:id/user_organizations/update_pay_periods_multiple',
    },
    updatePaymentInfo: {
      method: 'POST',
      url: '/user_organizations/:id/update_payment_info',
    },
    projects: {
      method: 'POST',
      url: '/organizations/:id/user_organizations/projects',
    },
    managers: {
      method: 'POST',
      url: '/organizations/:id/user_organizations/managers',
    },
    updateLimits: {
      method: 'POST',
      url: '/user_organizations/:id/update_limits',
    },
    submitIpLocationFeedback: {
      method: 'POST',
      url: '/user_organizations/:id/submit_ip_location_feedback',
    },
    projectRates: {
      method: 'GET',
      url: '/user_organizations/:id/project_rates',
    },
  },
});

export const OrganizationsResource = new Resource('/organizations/:id', {
  extra: {
    archive: {
      method: 'POST',
      url: '/organizations/:id/archive',
    },
    cancelSubscription: {
      method: 'POST',
      url: '/organizations/:id/cancel_subscription',
    },
    applyCoupon: {
      method: 'POST',
      url: '/organizations/:id/apply_coupon',
    },
    // TODO: remove with AR0228 (see Organizations::AcceptEssentialsChurnOfferAction)
    acceptEssentialsChurnOffer: {
      method: 'POST',
      url: '/organizations/:id/accept_essentials_churn_offer',
    },
    jobSites: {
      url: '/organizations/:id/job_sites',
    },
    members: {
      method: 'GET',
      url: '/organizations/:id/members',
    },
    enableTrackingMultiple: {
      method: 'POST',
      url: '/organizations/:id/user_organizations/enable_tracking_multiple',
    },
    disableTrackingMultiple: {
      method: 'POST',
      url: '/organizations/:id/user_organizations/disable_tracking_multiple',
    },
    projects: {
      method: 'GET',
      url: '/organizations/:id/projects',
    },
    newProject: {
      method: 'GET',
      url: '/organizations/:id/projects/new',
    },
    tasks: {
      method: 'GET',
      url: '/organizations/:id/tasks',
    },
    updateMfa: {
      method: 'POST',
      url: '/organizations/:id/update_mfa',
    },
    disable2fa: {
      method: 'POST',
      url: '/organizations/:organization_id/disable_2fa/:user_id',
    },
    resetPassword: {
      method: 'POST',
      url: '/organizations/:organization_id/reset_provisioned_password/:user_id',
    },
    requestNewPassword: {
      method: 'POST',
      url: '/organizations/:organization_id/request_password_reset/:user_id',
    },
    updatePlan: {
      method: 'POST',
      url: '/organizations/:id/update_plan',
    },
    removeCreditCard: {
      method: 'POST',
      url: '/organizations/:id/remove_card',
    },
    recordAttemptCancelation: {
      method: 'POST',
      url: '/organizations/:id/record_cancelation_attempt',
    },
    workTimeLimit: {
      method: 'GET',
      url: '/organizations/:id/settings/work_time_limits',
    },
    updateWorkLimitsAndExpectations: {
      method: 'POST',
      url: '/organizations/:id/update_work_limits_and_expectations_settings',
    },
    loadCustomFields: {
      method: 'GET',
      url: '/organizations/:id/custom_fields_settings',
    },
    updateCustomField: {
      method: 'PUT',
      url: '/organizations/:id/custom_fields_settings/custom_fields/:key/options',
    },
    updateRoleSetting: {
      method: 'POST',
      url: '/organizations/:id/settings/update_role_setting',
    },
    usageData: {
      method: 'GET',
      url: '/organizations/:id/usage_data',
    },
    updateSetting: {
      method: 'POST',
      url: '/organizations/:id/update_setting',
    },
    updateTimesheetApprovalsSettings: {
      method: 'PATCH',
      url: '/organizations/:id/update_timesheet_approvals_settings',
    },
    mergeSuggestions: {
      method: 'GET',
      url: '/organizations/:id/merge_members/suggestions',
    },
    mergeMembers: {
      method: 'POST',
      url: '/organizations/:id/merge_members',
    },
    mergeMembersSilent: {
      method: 'GET',
      url: '/organizations/:id/merge_members/silent_members',
    },
    declineMergeRecommendation: {
      method: 'POST',
      url: '/organizations/:id/merge_members/:recommendation_id/decline',
    },
    mergeMembersRecommendations: {
      method: 'GET',
      url: '/organizations/:id/merge_members/recommendations',
    },
  },
  links: {
    billing: '/organizations/:id/settings/billing',
    timesheetApprovalsSettings: '/organizations/:id/settings/timesheets/timesheet_approvals',
    plans: '/organizations/:id/plans',
    shifts: '/organizations/:id/schedules',
    syncIntegrationProjects: '/organizations/:id/sync_integrations_dialog.dialog',
    invoicesSettings: '/organizations/:id/settings/invoices',
    wizard: '/welcome/select_industry',
    index: '/organizations',
    allowedAppsSettings: '/organizations/:id/settings/timer_apps/client_platforms_allowed',
    profilesSettings: '/organizations/:id/settings/members',
    notificationsSettings: '/organizations/:id/settings/email_notifications',
    workLimitsAndExpectationsSettings: '/organizations/:id/settings/work_time_limits',
    newTeam: '/organizations/:id/teams?new',
    projects: '/organizations/:id/projects',
    rolePermissions: '/organizations/:id/settings/role_permissions',
  },
});

export const DownloadResource = new Resource('/download', {
  links: {
    corporate: '/download/corporate',
  },
});

export const TeamsResource = new Resource('/teams/:id', {
  extra: {
    create: {
      method: 'POST',
      url: '/organizations/:id/teams',
    },
    members: {
      method: 'GET',
      url: '/organizations/:organization_id/teams/members',
    },
    leadEmails: {
      method: 'GET',
      url: '/teams/:id/lead_emails',
    },
  },
  links: {
    addMembers: '/teams/:id/members/new',
  },
});

export const TimeEntriesResource = new Resource('/time_entries/:id', {
  extra: {
    index: {
      method: 'GET',
      url: '/organizations/:organizationId/time_entries/:view',
    },
    deleteMultiple: {
      method: 'DELETE',
      url: '/organizations/all/time_entries/delete_multiple',
    },
    check: {
      method: 'POST',
      url: '/time_entries/:id/check',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organization_id/time_entries/members',
    },
    projects: {
      method: 'POST',
      url: '/organizations/:organization_id/time_entries/projects',
    },
    approveManualTimeRequest: {
      method: 'POST',
      url: '/time_entries/approve_manual_time_request',
    },
    updateWithManualTimeRequests: {
      method: 'PATCH',
      url: '/time_entries/:id/update_with_manual_time_requests',
    },
  },
});

export const UserSettingsResource = new Resource('/users/:id', {
  extra: {
    updateInvoicingSettings: {
      method: 'PATCH',
      url: '/users/:id/update_invoicing',
    },
  },
});

export const GettingStartedResource = new Resource('/getting_started/:organizationId', {
  only: [], // No default CRUD methods
  extra: {
    updateSubstep: {
      method: 'POST',
      url: '/organizations/:organizationId/getting_started/update_substep',
    },
    previousSubstep: {
      method: 'POST',
      url: '/organizations/:organizationId/getting_started/previous_substep',
    },
    substep: {
      method: 'GET',
      url: '/organizations/:organizationId/getting_started/substep',
    },
  },
});

export const QuickStartResource = new Resource('/quick_start/:organizationId', {
  only: [], // No default CRUD methods
  extra: {
    recordStepProgress: {
      method: 'POST',
      url: '/organizations/:organizationId/quick_start/record_step_progress',
    },
  },
});

export const InsightsOnboardingResource = new Resource('/insights_onboarding/:organizationId', {
  only: [], // No default CRUD methods
  extra: {
    updateSubstep: {
      method: 'POST',
      url: '/organizations/:organizationId/insights_onboarding/update_substep',
    },
    previousSubstep: {
      method: 'POST',
      url: '/organizations/:organizationId/insights_onboarding/previous_substep',
    },
    substep: {
      method: 'GET',
      url: '/organizations/:organizationId/insights_onboarding/substep',
    },
  },
});

export const SmartNotificationsSettingsResource = new Resource(
  '/organizations/:organizationId/smart_notification_rules',
  {
    extra: {
      load: {
        method: 'GET',
        url: '/organizations/:organizationId/smart_notification_rules/',
      },
      loadTools: {
        method: 'GET',
        url: '/organizations/:organizationId/smart_notification_rules/tool_list',
      },
      slackStatus: {
        method: 'GET',
        url: '/organizations/:organizationId/smart_notification_rules/slack_users',
      },
      create: {
        method: 'POST',
        url: '/organizations/:organizationId/smart_notification_rules',
      },
      alerts: {
        method: 'POST',
        url: '/organizations/:organizationId/smart_notification_rules/alerts',
      },
      edit: {
        method: 'PUT',
        url: '/organizations/:organizationId/smart_notification_rules/:smartNotificationId/',
      },
      toggle: {
        method: 'POST',
        url: '/organizations/:organizationId/smart_notification_rules/:smartNotificationId/toggle',
      },
      delete: {
        method: 'DELETE',
        url: '/organizations/:organizationId/smart_notification_rules/:smartNotificationId/',
      },
      members: {
        method: 'POST',
        url: '/organizations/:organizationId/smart_notification_rules/members',
      },
      suggest: {
        method: 'POST',
        url: '/organizations/:organizationId/smart_notification_rules/suggest',
      },
    },
    links: {
      dialog: '/smart_notification_rules/cta_dialog.dialog',
    },
  },
);

export const LocationsResource = new Resource(ORGANIZATION_LOCATIONS_ACTIVITIES_URL, {
  only: ['get'],
});

export const InsightsResource = new Resource('/organizations/:organizationId/insights/:dashboardType', {
  only: ['get'],
  extra: {
    widget: {
      method: 'GET',
      url: '/organizations/:organizationId/insights/widget/:dashboardType/:widgetKey',
    },
    load: {
      method: 'GET',
      url: '/organizations/:organizationId/insights',
    },
    benchmarksAveragesLegend: {
      method: 'GET',
      url: `/organizations/:organizationId/insights/context_metadata`,
    },
    engagementScores: {
      method: 'POST',
      url: '/organizations/:organizationId/insights/engagement_scores',
    },
    extendedCancellationEligibility: {
      method: 'GET',
      url: '/organizations/:organizationId/insights/eligible_for_extended_cancellation',
    },
    trackRetentionOffer: {
      method: 'POST',
      url: '/organizations/:organizationId/insights/track_retention_offer',
    },
    usageData: {
      method: 'GET',
      url: '/organizations/:organizationId/insights/usage_data',
    },
    cancellationReasons: {
      method: 'GET',
      url: '/organizations/:organizationId/insights_cancellation_reasons',
    },
    saveCancellationForm: {
      method: 'POST',
      url: '/organizations/:organizationId/insights_cancellation_reasons',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organizationId/insights/members',
    },
    teams: {
      method: 'POST',
      url: '/organizations/:organizationId/insights/teams',
    },
  },
  links: {
    insights: '/organizations/:organizationId/insights',
  },
});

export const UnusualActivitiesResource = new Resource('/organizations/:organizationId/unusual_activities', {
  only: [],
  extra: {
    markReviewed: {
      method: 'POST',
      url: '/organizations/:organizationId/unusual_activities/:userId/mark_as_reviewed',
    },
    markAllReviewed: {
      method: 'POST',
      url: '/organizations/:organizationId/unusual_activities/mark_all_as_reviewed',
    },
  },
});

export const AppUrlsMonitoringSettingsEnableResource = new Resource('/', {
  extra: {
    monitoring: {
      method: 'POST',
      url: '/organizations/:id/addons',
    },
    app_urls: {
      method: 'POST',
      url: '/organizations/:id/update_setting',
    },
  },
});

export const TimeOffBalancesResource = new Resource('/organizations/:organizationId/settings/time_off_balances', {});

// FIXME: This should be moved to a full URL like structure rather
//        than a shallow URL structure.
// Bernat Jufré 2022-03-04
export const JobSitesResource = new Resource('/job_sites/:id', {
  extra: {
    create: {
      method: 'POST',
      url: '/organizations/:organizationId/job_sites',
    },
    archive: {
      method: 'POST',
      url: '/job_sites/:id/archive',
    },
    addMember: {
      method: 'POST',
      url: '/job_sites/:id/add_member',
    },
    updateMember: {
      method: 'POST',
      url: '/job_sites/:id/update_member',
    },
    removeMember: {
      method: 'POST',
      url: '/job_sites/:id/remove_member',
    },
  },
});

export const WorkOrdersResource = new Resource(WORK_ORDERS_URL, {
  extra: {
    ...['archive', 'restore'].reduce(
      (actions, action) => ({
        ...actions,
        [action]: {
          method: 'PATCH',
          url: `${WORK_ORDERS_URL}/${action}`,
        },
      }),
      {},
    ),
    ...['check', 'next_number'].reduce(
      (actions, action) => ({
        ...actions,
        [camelCase(action)]: {
          method: action === 'check' ? 'POST' : 'GET',
          url: `/organizations/:organizationId/work_orders/${action}`,
        },
      }),
      {},
    ),
  },
});

export const WorkOrdersJobsResource = new Resource(`${WORK_ORDERS_URL}/jobs/:jobId`, {
  extra: {
    ...['complete', 'reopen'].reduce(
      (actions, action) => ({
        ...actions,
        [action]: {
          method: 'PATCH',
          url: `${WORK_ORDERS_URL}/jobs/:jobId/${action}`,
        },
      }),
      {},
    ),
    edit: {
      method: 'GET',
      url: `${WORK_ORDERS_URL}/jobs/:jobId/edit`,
    },
  },
});

export const WorkOrdersJobSchedulesResource = new Resource(`${WORK_ORDERS_URL}/job_schedules/:scheduleId`);

export const MapUsersResource = new Resource(`${ORGANIZATION_MAP_URL}/users/:userId`, {
  only: ['get'],
  extra: {
    locations: {
      method: 'GET',
      url: `${ORGANIZATION_MAP_URL}/users/:userId/locations`,
    },
  },
});

export const MapSitesResource = new Resource(`${ORGANIZATION_MAP_URL}/sites/:siteId`, {
  only: ['get'],
});

export const ExpensesResource = new Resource('/organizations/:organizationId/expenses/:id');

export const BenchmarksResource = new Resource('/organizations/:orgId/activities/benchmarks', {
  only: ['get'],
  extra: {
    loadDialog: {
      method: 'GET',
      url: '/organizations/:orgId/activities/benchmarks_dialog',
    },
  },
});

export const ActivitiesResource = new Resource('/organizations/:organizationId/activities', {
  only: ['get'],
  extra: {
    hideDialog: {
      method: 'GET',
      url: '/activities/hide_dialog',
    },
    showDialog: {
      method: 'GET',
      url: '/activities/show_dialog',
    },
    deleteActivity: {
      method: 'DELETE',
      url: '/activities/delete_activity',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organization_id/activities/members',
    },
  },
});

export const ScreensResource = new Resource('/screens/:screenId', {
  extra: {
    deleteScreens: {
      method: 'DELETE',
      url: '/screens/delete_shots',
    },
    count: {
      method: 'GET',
      url: '/screens/count',
    },
  },
});

export const NotesResource = new Resource('/notes/:id', {
  extra: {
    get: {
      method: 'GET',
      url: '/notes/time_slot?v2=true',
    },
  },
});

export const SettingsResource = new Resource('/organizations/:organizationId/settings', {
  extra: {
    updateInvoice: {
      method: 'POST',
      url: '/organizations/:organizationId/update_invoice_settings',
    },
    getInsights: {
      method: 'GET',
      url: '/organizations/:organizationId/settings/insights',
    },
    updateInsights: {
      method: 'PUT',
      url: '/organizations/:organizationId/settings/insights',
    },
  },
});

export const GlobalTodosResource = new Resource('/organizations/:organizationId/global_todos', {
  extra: {
    edit: {
      method: 'GET',
      url: '/organizations/:organizationId/global_todos/:id/edit',
    },
    projects: {
      method: 'POST',
      url: '/organizations/:organizationId/global_todos/projects',
    },
  },
});

export const TimesheetsResource = new Resource('/organizations/:organizationId/timesheets', {
  extra: {
    members: {
      method: 'GET',
      url: '/organizations/:organization_id/timesheets/members',
    },
    teams: {
      method: 'GET',
      url: '/organizations/:organization_id/timesheets/teams',
    },
    periodsForRange: {
      method: 'GET',
      url: '/organizations/:organization_id/timesheets/periods_for_range',
    },
    generateTimesheets: {
      method: 'POST',
      url: '/organizations/:organization_id/timesheets/generate_timesheets',
    },
    members_dropdown: {
      method: 'POST',
      url: '/organizations/:organization_id/timesheets/members_dropdown',
    },
    update_entry_reasons: {
      method: 'POST',
      url: '/organizations/:organization_id/settings/timesheets/update_entry_reasons',
    },
    timeEntries: {
      method: 'GET',
      url: '/timesheets/:timesheetId/time_entries',
    },
  },
  links: {
    timesheet: '/timesheets/:timesheetId',
  },
});

export const TimeOffRequestsResource = new Resource('/organizations/:organizationId/time_off_requests', {
  extra: {
    members: {
      method: 'GET',
      url: '/organizations/:organization_id/time_off_requests/members',
    },
    members_dropdown: {
      method: 'POST',
      url: '/organizations/:organization_id/time_off_requests/members_dropdown',
    },
    teams: {
      method: 'GET',
      url: '/organizations/:organization_id/time_off_requests/teams',
    },
    new: {
      method: 'GET',
      url: '/organizations/:organizationId/time_off_requests/new',
    },
    user_balance: {
      method: 'GET',
      url: '/time_off_requests/user_balance?id=:id&organization_id=:organizationId&time_off_policy_id=:timeOffPolicyId&user_id=:userId&starts_at_date=:startsAtDate&stops_at_date=:stopsAtDate&amount_used=:amountUsed&first_year_amount=:firstYearAmount&second_year_amount=:secondYearAmount',
    },
    create: {
      method: 'POST',
      url: '/organizations/:organizationId/time_off_requests',
    },
    update: {
      method: 'PUT',
      url: '/time_off_requests/:id',
    },
    edit: {
      method: 'GET',
      url: '/time_off_requests/:id/edit',
    },
    show: {
      method: 'GET',
      url: '/time_off_requests/:id',
    },
    deny: {
      method: 'GET',
      url: '/time_off_requests/:id/deny',
    },
    approve: {
      method: 'GET',
      url: '/time_off_requests/:id/approve',
    },
    shiftOverlaps: {
      method: 'GET',
      url: '/organizations/:organizationId/time_off_requests/shift_count_on_request',
    },
    update_status: {
      method: 'POST',
      url: '/time_off_requests/:id/update_status?organization_id=:organizationId',
    },
  },
});

export const AttendanceSchedulesResource = new Resource('/organizations/:organizationId/schedules', {
  extra: {
    edit: {
      method: 'GET',
      url: '/schedules/:scheduleId/edit',
    },
    create: {
      method: 'POST',
      url: '/organizations/:organizationId/schedules',
    },
    calendarResources: {
      method: 'GET',
      url: '/organizations/:organizationId/schedules/calendar_resources',
    },
    eventDetails: {
      method: 'GET',
      url: '/organizations/:organizationId/schedules/event_details/:eventType/:eventId',
    },
    countShifts: {
      method: 'GET',
      url: '/organizations/:organizationId/schedules/count_shifts',
    },
    membersWithoutShifts: {
      method: 'POST',
      url: '/organizations/:organizationId/schedules/members_without_shifts',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organization_id/schedules/members',
    },
    teams: {
      method: 'POST',
      url: '/organizations/:organization_id/schedules/teams',
    },
  },
});

export const HolidaysResource = new Resource('/organizations/:organizationId/holidays', {
  extra: {
    create: {
      method: 'POST',
      url: '/organizations/:organizationId/holidays',
    },
    update: {
      method: 'PUT',
      url: '/holidays/:id',
    },
    updateMembers: {
      method: 'PUT',
      url: '/holidays/:id/update_members',
    },
    checkCsvFile: {
      method: 'POST',
      url: '/organizations/:organizationId/holidays/validate_members_csv',
    },
    members: {
      method: 'GET',
      url: '/organizations/:organization_id/holidays/members_data',
    },
    new: {
      method: 'GET',
      url: '/organizations/:organizationId/holidays/new',
    },
    edit: {
      method: 'GET',
      url: '/holidays/:id/edit',
    },
    shiftOverlaps: {
      method: 'POST',
      url: '/organizations/:organizationId/holidays/shift_count_on_request',
    }, // We're reusing an existing endpoint, so we're keeping its POST method (instead of GET).
    validate: {
      method: 'POST',
      url: '/organizations/:organizationId/holidays/validate',
    },
    user_holidays: {
      method: 'GET',
      url: '/organizations/:organizationId/holidays/user_holidays?user_id=:userId&starts_at_date=:startsAtDate&stops_at_date=:stopsAtDate',
    },
    bulkCreate: {
      method: 'POST',
      url: '/organizations/:organizationId/holidays/bulk_create',
    },
  },
});

export const TimeOffPoliciesResource = new Resource('/organizations/:organizationId/time_off_policies', {
  extra: {
    show: {
      method: 'GET',
      url: '/time_off_policies/:id',
    },
    create: {
      method: 'POST',
      url: '/organizations/:organizationId/time_off_policies',
    },
    edit: {
      method: 'GET',
      url: '/time_off_policies/:id/edit',
    },
    update: {
      method: 'PUT',
      url: '/time_off_policies/:id',
    },
    members: {
      method: 'GET',
      url: '/organizations/:organization_id/time_off_policies/members_data',
    },
    checkCsv: {
      method: 'POST',
      url: '/organizations/:organization_id/time_off_policies/check_csv?id=:id',
    },
    user_policies: {
      method: 'GET',
      url: '/organizations/:organizationId/time_off_policies/user_policies?user_id=:userId',
    },
  },
});

export const IntegrationUsersResource = new Resource('/', {
  extra: {
    users: {
      method: 'GET',
      url: '/integrations/:integrationId/remote_users',
    },
    submit: {
      method: 'POST',
      url: '/integrations/:integrationId/link_users',
    },
    unlink: {
      method: 'POST',
      url: '/integrations/:integrationId/unlink_user',
    },
    unlinkMultiple: {
      method: 'POST',
      url: '/integrations/:integrationId/unlink_multiple_users',
    },
    members: {
      method: 'POST',
      url: '/integrations/:integrationId/members',
    },
  },
});

export const AutoTrackRulesResource = new Resource('/organizations/:organizationId/settings', {
  extra: {
    index: {
      method: 'GET',
      url: '/organizations/:organizationId/settings/timer_apps/auto_track',
    },
    archive: {
      method: 'POST',
      url: '/organizations/:organizationId/auto_track_rules/:id/archive',
    },
    restore: {
      method: 'POST',
      url: '/organizations/:organizationId/auto_track_rules/:id/restore',
    },
    create: {
      method: 'POST',
      url: '/organizations/:organizationId/settings/timer_apps/auto_track',
    },
    edit: {
      method: 'GET',
      url: '/organizations/:organizationId/settings/timer_apps/auto_track/:id/edit',
    },
    members: {
      method: 'POST',
      url: '/organizations/:organizationId/settings/timer_apps/auto_track/members',
    },
    update: {
      method: 'PUT',
      url: '/organizations/:organizationId/settings/timer_apps/auto_track/:id',
    },
    hideBanner: {
      method: 'PATCH',
      url: '/user/hide_corporate_upsell_banner',
    },
  },
});

export const FileImportsResource = new Resource('/file_imports', {
  only: ['create'],
  extra: {
    fetch: {
      method: 'POST',
      url: '/file_imports/:id/fetch',
    },
  },
});

export const PopoverAvatarUserCardResource = new Resource('/organizations/:organizationId/members/:userId.json', {
  only: ['get'],
  links: {
    memberProfile: '/organizations/:organizationId/members/:userId?tab=:tab',
    organizationUserActivities: '/organizations/:organizationId/activities?filters[user]=:userId',
  },
});

export const WorkBreakPoliciesResource = new Resource('/organizations/:organizationId/work_break_policies', {
  only: [],
  extra: {
    members: {
      method: 'POST',
      url: '/organizations/:organization_id/work_break_policies/members',
    },
  },
});

export const BackgroundTasksResource = new Resource('/organizations/:organizationId/background_tasks/:id', {
  only: ['get'],
});

export const InvitesResource = new Resource('/organizations/:id/invites/', {
  extra: {
    bulkCreate: {
      method: 'POST',
      url: '/organizations/:id/invites/bulk_create',
    },
    updatePayRate: {
      method: 'POST',
      url: '/invites/:id/update_pay_rate',
    },
  },
});

export const PaidAddonsResource = new Resource('/organizations/:organization_id/paid_addons', {
  extra: {
    applyCoupon: {
      method: 'POST',
      url: '/organizations/:organizationId/paid_addons/:addonSym/apply_coupon',
    },
    extendTrial: {
      method: 'POST',
      url: '/organizations/:organizationId/paid_addons/:addonSym/extend_trial',
    },
    disableAddon: {
      method: 'POST',
      url: '/organizations/:organizationId/paid_addons/:addonSym/disable',
    },
  },
});

export const SSOResource = new Resource('/organizations/:organizationId/settings/sso', {
  only: ['create', 'show'],
  extra: {
    destroy: {
      method: 'DELETE',
      url: '/organizations/:organizationId/settings/sso',
    },
  },
});

export const AnalyticsResource = new Resource('/analytics', {
  only: ['create'],
});

export const TeamPaymentsResource = new Resource('/team_payments/:id', {
  links: {
    show: '/team_payments/:id',
  },
});

export default {
  BenchmarksResource,
  ClientsResource,
  ProjectsResource,
  ReportsResource,
  SavedReportFiltersResource,
  ScheduledReportsResource,
  UserResource,
  UserOrganizationsResource,
  TeamsResource,
  LocationsResource,
  OrganizationsResource,
  TimeEntriesResource,
  TimeOffBalancesResource,
  JobSitesResource,
  MapUsersResource,
  MapSitesResource,
  ExpensesResource,
  WorkOrdersResource,
  WorkOrdersJobsResource,
  NotesResource,
  SettingsResource,
  AttendanceSchedulesResource,
  FileImportsResource,
  PopoverAvatarUserCardResource,
  AutoTrackRulesResource,
  TimesheetsResource,
  WorkBreakPoliciesResource,
  GettingStartedResource,
  InvitesResource,
  HolidaysResource,
  SSOResource,
  AnalyticsResource,
  TeamPaymentsResource,
};
